import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState, useMemo } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
const ShelfPictorialView = ({
  onOpenFinancials
}) => {
  const masked = useMaskNames();
  const [inventoryData, setInventoryData] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [shelfImages, setShelfImages] = useState({});
  const [visibleImages, setVisibleImages] = useState({});
  const [popupImage, setPopupImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({
    x: 0,
    y: 0
  });
  const shelfChecks = JSON.parse(localStorage.getItem("shelf_checks") || "[]");
  const members = JSON.parse(localStorage.getItem("members") || "[]");
  const matched = members.find(m => m.pharmacyName === selectedPharmacy);
  const pharmacyId = matched ? matched.pharmacyId : "";
  let filteredShelves = shelfChecks;
  if (selectedPharmacy) {
    filteredShelves = shelfChecks.filter(shelf => shelf.pharmacyId === pharmacyId);
  }
  useEffect(() => {
    const fetchInventoryData = async () => {
      const groupId = localStorage.getItem("userId");
      if (!groupId) return;
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_ORIGIN}/ggetInventory?groupId=${groupId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        let data = await response.json();
        data = data.filter(item => !item.pharmacyName?.startsWith("Warehouse") && (item.status || "").trim() === "On shelf");
        setInventoryData(data);
        if (!selectedPharmacy && data.length > 0) {
          setSelectedPharmacy(data[0].pharmacyName);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchInventoryData();
  }, [selectedPharmacy]);
  useEffect(() => {
    const fetchShelfImages = async () => {
      if (!pharmacyId) return;
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${API_ORIGIN}/getShelfPhotos?pharmacyId=${pharmacyId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();
        const map = {};
        data.forEach(item => {
          const key = (item._id || item.shelfName || "").trim().toLowerCase();
          if (!map[key]) {
            map[key] = {
              image: item.image
            };
          }
        });
        setShelfImages(map);
      } catch (err) {
        console.error(err);
      }
    };
    fetchShelfImages();
  }, [pharmacyId]);
  const normaliseShelfKey = value => (value || "").trim().toLowerCase();
  const buildImageSrc = image => {
    if (!image) return "";
    if (image.startsWith("data:image")) return image;
    if (image.startsWith("http")) return image;
    return `data:image/jpeg;base64,${image}`;
  };
  const getOptimisedShelfImage = location => {
    const record = shelfImages[normaliseShelfKey(location)];
    return record ? {
      src: buildImageSrc(record.image)
    } : null;
  };
  const imageRefs = React.useRef({});
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const key = entry.target.getAttribute("data-key");
        if (entry.isIntersecting && key) {
          setVisibleImages(prev => ({
            ...prev,
            [key]: true
          }));
          observer.unobserve(entry.target);
        }
      });
    });
    Object.values(imageRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [filteredShelves, shelfImages]);
  const locationList = useMemo(() => {
    return [...new Set(filteredShelves.map(shelf => shelf.description))].sort();
  }, [filteredShelves]);
  const pharmacyList = [...new Set(inventoryData.map(item => item.pharmacyName))];
  const getWeeksUntil = dateStr => {
    if (!dateStr) return "";
    return Math.floor((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24 * 7));
  };
  const getWeeksSince = dateStr => {
    if (!dateStr) return "";
    return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24 * 7));
  };
  const getExpiryColor = weeks => {
    if (weeks < -500) return "white";
    const ratio = Math.max(0, Math.min(12, weeks)) / 12;
    return `rgb(${Math.round(255 * (1 - ratio))}, ${Math.round(200 * ratio)}, 0)`;
  };
  const getLocationSummary = location => {
    let items = inventoryData.filter(item => item.pharmacyName === selectedPharmacy && (item.location || "").trim().toLowerCase() === location.trim().toLowerCase());
    return {
      totalPackets: items.reduce((s, i) => s + parseInt(i.balance || 0), 0),
      uniqueDrugs: new Set(items.map(i => i.vmppNm)).size,
      totalValue: items.reduce((s, i) => s + (i.price || 0), 0),
      items
    };
  };
  return <div style={{
    padding: "20px"
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
        <h1>Shelf contents - pictorial view</h1>
        <div style={{
        display: "flex",
        gap: "8px",
        alignItems: "center"
      }}>
          {/* The way back. This screen has no menu entry of its own, so without
              this button it can only be left by using the browser's back. */}
          {onOpenFinancials && <button onClick={onOpenFinancials} title="Back to the scanned items list." style={{
          padding: "6px 12px",
          fontWeight: 600
        }}>
              Financial view
            </button>}
          <MaskToggleButton />
        </div>
      </div>

      <div style={{
      marginBottom: "20px"
    }}>
        <label>Pharmacy: </label>
        <select value={selectedPharmacy} onChange={e => setSelectedPharmacy(e.target.value)}>
          {pharmacyList.map((ph, i) => <option key={i} value={ph}>{displayName(ph, masked)}</option>)}
        </select>
        <input type="text" placeholder="Search drug..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
        marginLeft: "20px",
        padding: "4px"
      }} />
      </div>

      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "20px"
    }}>
        {locationList.map((location, index) => {
        const summary = getLocationSummary(location);
        return <div key={index} style={{
          border: "1px solid black",
          padding: "10px",
          width: "300px",
          height: "300px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column"
        }}>
              
              <h2>{location}</h2>

              <div ref={el => el && (imageRefs.current[normaliseShelfKey(location)] = el)} data-key={normaliseShelfKey(location)} style={{
            height: "100px",
            background: "#ddd",
            marginBottom: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
                {visibleImages[normaliseShelfKey(location)] ? getOptimisedShelfImage(location) ? <img src={getOptimisedShelfImage(location).src} style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              cursor: "pointer"
            }} onClick={() => {
              setPopupImage(getOptimisedShelfImage(location).src);
              setZoom(1);
              setPosition({
                x: 0,
                y: 0
              });
            }} /> : <span>No Image</span> : <span>Loading...</span>}
              </div>

              <div style={{
            fontSize: "12px"
          }}>
                <div>Total items: {summary.totalPackets}</div>
                <div>Different drugs: {summary.uniqueDrugs}</div>
                <div>£{(summary.totalValue / 100).toFixed(2)}</div>
              </div>

              <div style={{
            overflowY: "auto",
            fontSize: "11px"
          }}>
                <table style={{
              width: "100%"
            }}>
                  <tbody>
                    {summary.items.map((item, i) => <tr key={i} style={{
                  background: getExpiryColor(getWeeksUntil(item.expiryDate))
                }}>
                        <td>{item.vmppNm}</td>
                        <td>{(item.price / 100).toFixed(2)}</td>
                        <td>{getWeeksSince(item.firstScanned)}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

            </div>;
      })}
      </div>

      {popupImage && <div onClick={() => setPopupImage(null)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.85)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999
    }}>
          {/* CONTROLS */}
          <div onClick={e => e.stopPropagation()} style={{
        position: "fixed",
        top: 20,
        right: 20,
        display: "flex",
        gap: "10px",
        zIndex: 10000,
        background: "rgba(0,0,0,0.6)",
        padding: "10px",
        borderRadius: "6px"
      }}>
            <button onClick={() => setZoom(z => Math.min(5, z + 0.2))}>+</button>
            <button onClick={() => setZoom(z => Math.max(0.5, z - 0.2))}>−</button>
            <button onClick={() => {
          setZoom(1);
          setPosition({
            x: 0,
            y: 0
          });
        }}>Reset</button>
            <button onClick={() => setPopupImage(null)}>✕</button>
          </div>

          <img src={popupImage} onClick={e => e.stopPropagation()} onWheel={e => {
        e.preventDefault();
        setZoom(z => Math.max(0.5, Math.min(5, z - e.deltaY * 0.001)));
      }} onMouseDown={e => {
        setDragging(true);
        setStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y
        });
      }} onMouseMove={e => {
        if (dragging) {
          setPosition({
            x: e.clientX - start.x,
            y: e.clientY - start.y
          });
        }
      }} onMouseUp={() => setDragging(false)} onMouseLeave={() => setDragging(false)} style={{
        transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
        cursor: dragging ? "grabbing" : "grab",
        maxWidth: "none",
        maxHeight: "none"
      }} />
        </div>}
    </div>;
};
export default ShelfPictorialView;
